/**
 * 계측(Measure) 데이터 관리 Hook
 * Feature: #393
 *
 * GraphQL API를 통해 계측 데이터 CRUD 수행
 * 오프라인: IndexedDB 캐시에서 조회 + 로컬 수정 후 동기화 큐에 추가 (Feature #411)
 * @see packages/web/hooks/useMeasures.ts (웹 버전 참고)
 */
import { useQuery, useMutation } from "@apollo/client";
import { useState, useEffect, useCallback } from "react";
import { MeasureType, measureTypeValueToGraphQLKey, getMeasureController } from "@ict/shared";
import type { MeasureConfig, DiscussionOverrides } from "@ict/shared";
import type { MeasureDiscusItem } from "@ict/shared";
import {
  MACHINE_MEASURES_QUERY,
  ADD_MEASURE_MUTATION,
  UPDATE_MEASURE_MUTATION,
  DELETE_MEASURE_MUTATION,
  type MachineMeasuresData,
  type MeasureResponse,
  type AddMeasureData,
  type AddMeasureVariables,
  type UpdateMeasureData,
  type UpdateMeasureVariables,
  type DeleteMeasureData,
  type DeleteMeasureVariables,
} from "../lib/graphql/measures";
import { getCachedMachine, getCachedMachinesByProject, cacheMachine } from "../lib/db/machines";
import { addToSyncQueue, getSyncQueue, adjustMeasurePhotoQueue } from "../lib/db/changes";
import { useNetworkStatus } from "./useNetworkStatus";
import { createLogger } from "@ict/shared";

const logger = createLogger({ module: "useMeasures" });

/** MeasuresData 내 필드명 (24개 계측 타입) */
type MeasuresFieldName = keyof NonNullable<MachineMeasuresData["machineMeasures"]>;

/** MeasureType enum 값을 필드명으로 변환 */
function measureTypeToFieldName(type: MeasureType): MeasuresFieldName {
  return type as MeasuresFieldName;
}

/**
 * 클라이언트 측 자동 판정 적용 (오프라인용)
 * 서버의 applyAutoJudgment와 동일한 로직
 * measureDiscus가 있으면 회사별 판정 코멘트를 DiscussionOverrides로 적용
 * 동기화 시 서버가 재판정하므로 최종 데이터는 정확
 */
function applyClientJudgment(
  measureType: MeasureType,
  data: Record<string, unknown>,
  measureDiscus?: MeasureDiscusItem[]
): Record<string, unknown> {
  const ControllerClass = getMeasureController(measureType);
  if (!ControllerClass) return data;

  const config: MeasureConfig = {};
  if (data.frequencyBand) {
    config.extra = { frequencyBand: data.frequencyBand as string };
  }

  // 캐시된 measureDiscus에서 해당 타입의 판정 코멘트를 DiscussionOverrides로 변환
  let overrides: DiscussionOverrides | undefined;
  if (measureDiscus?.length) {
    const discusItem = measureDiscus.find((d) => d.key === measureType);
    if (discusItem) {
      overrides = {
        common: discusItem.common,
        good: discusItem.good,
        caution: discusItem.caution,
        bad: discusItem.bad,
        na: discusItem.na,
      };
    }
  }

  const controller = new ControllerClass(data, config, overrides);
  return controller.getData() as Record<string, unknown>;
}

interface UseMeasuresOptions {
  projectId: string;
  docKey: string;
  machineId?: string;
}

/**
 * 로컬 IndexedDB 캐시의 measures 데이터를 업데이트하고 state에 반영
 */
async function updateLocalMeasuresCache(
  projectId: string,
  docKey: string,
  machineId: string,
  fieldName: MeasuresFieldName,
  updater: (existing: Record<string, unknown>[] | undefined) => Record<string, unknown>[],
  cachedMachine?: Awaited<ReturnType<typeof getCachedMachine>>
): Promise<MachineMeasuresData["machineMeasures"]> {
  const cached = cachedMachine ?? (await getCachedMachine(projectId, docKey, machineId));
  if (!cached) return null;

  const currentMeasures = cached.measures ?? {};
  const existingArray = currentMeasures[fieldName] as Record<string, unknown>[] | undefined;
  const updatedArray = updater(existingArray);

  const updatedMeasures = {
    ...currentMeasures,
    [fieldName]: updatedArray,
  };

  await cacheMachine({
    ...cached,
    measures: updatedMeasures,
    cachedAt: Date.now(),
  });

  return updatedMeasures;
}

/**
 * 계측 데이터 관리 Hook
 *
 * - useQuery: 설비별 전체 계측 데이터 조회 (온라인)
 * - 오프라인: IndexedDB 캐시에서 measures 필드 조회
 * - useMutation: 추가/수정/삭제 (온라인: Apollo 캐시, 오프라인: IndexedDB + syncQueue)
 * - getMeasuresByType: 특정 타입 데이터 조회
 * - getTotalMeasureCount: 전체 항목 수 계산
 */
export function useMeasures({ projectId, docKey, machineId }: UseMeasuresOptions) {
  const { isOffline, isLoading: networkLoading } = useNetworkStatus();
  const [cachedMeasures, setCachedMeasures] =
    useState<MachineMeasuresData["machineMeasures"]>(null);
  const [cacheLoading, setCacheLoading] = useState(true);
  const [isFromCache, setIsFromCache] = useState(false);

  // 오프라인 시 IndexedDB에서 캐시된 계측 데이터 로드
  useEffect(() => {
    async function loadCachedMeasures() {
      if (!projectId || !docKey || !machineId) {
        setCacheLoading(false);
        return;
      }

      setCacheLoading(true);
      try {
        const cached = await getCachedMachine(projectId, docKey, machineId);
        if (cached?.measures) {
          setCachedMeasures(cached.measures);
          setIsFromCache(true);
        }
      } catch (err) {
        logger.error("계측 캐시 로드 실패", err);
      } finally {
        setCacheLoading(false);
      }
    }
    loadCachedMeasures();
  }, [projectId, docKey, machineId]);

  // 조회 쿼리 (machineId가 있을 때만, 온라인 시에만 실행)
  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery<MachineMeasuresData>(MACHINE_MEASURES_QUERY, {
    variables: { projectId, docKey, machineId },
    skip: !machineId || networkLoading || isOffline,
    fetchPolicy: "cache-and-network",
  });

  // 온라인 데이터 수신 시 IndexedDB 캐시에 measures 저장
  useEffect(() => {
    async function saveMeasuresToCache() {
      if (!isOffline && data?.machineMeasures && projectId && docKey && machineId) {
        try {
          const existing = await getCachedMachine(projectId, docKey, machineId);
          if (existing) {
            await cacheMachine({
              ...existing,
              measures: data.machineMeasures,
              cachedAt: Date.now(),
            });
          }
          setCachedMeasures(data.machineMeasures);
          setIsFromCache(false);
        } catch (err) {
          logger.error("계측 캐시 저장 실패", err);
        }
      }
    }
    saveMeasuresToCache();
  }, [isOffline, data, projectId, docKey, machineId]);

  // 최종 measures 데이터: 서버 데이터 우선, 없으면 캐시
  const measures = data?.machineMeasures ?? cachedMeasures ?? null;

  // 추가 Mutation
  const [addMeasureMutation, { loading: addLoading }] = useMutation<
    AddMeasureData,
    AddMeasureVariables
  >(ADD_MEASURE_MUTATION);

  // 수정 Mutation
  const [updateMeasureMutation, { loading: updateLoading }] = useMutation<
    UpdateMeasureData,
    UpdateMeasureVariables
  >(UPDATE_MEASURE_MUTATION);

  // 삭제 Mutation
  const [deleteMeasureMutation, { loading: deleteLoading }] = useMutation<
    DeleteMeasureData,
    DeleteMeasureVariables
  >(DELETE_MEASURE_MUTATION);

  // 계측 항목 추가 (온라인: GraphQL mutation, 오프라인: 로컬 캐시 + syncQueue)
  const addMeasure = useCallback(
    async (
      measureType: MeasureType,
      measureData: Record<string, unknown>,
      expectedVersion?: number
    ): Promise<MeasureResponse> => {
      if (!machineId) {
        return { success: false, error: "설비를 선택해주세요." };
      }

      const graphqlMeasureType = measureTypeValueToGraphQLKey(measureType);
      const fieldName = measureTypeToFieldName(measureType);

      // 오프라인: 로컬 캐시에 즉시 추가 + syncQueue에 create 항목 추가
      if (isOffline) {
        try {
          // 캐시된 measureDiscus 조회 (회사별 판정 코멘트)
          const cached = await getCachedMachine(projectId, docKey, machineId);
          let measureDiscus = cached?.project?.measureDiscus;

          // 오프라인 생성 설비에 measureDiscus가 없으면 같은 프로젝트 다른 설비에서 조회 (#469)
          if (!measureDiscus) {
            const siblings = await getCachedMachinesByProject(projectId);
            measureDiscus = siblings.find((m) => m.project?.measureDiscus)?.project?.measureDiscus;
          }

          let newMeasureIndex = 0;
          let judgedData: Record<string, unknown> = measureData;
          const updatedMeasures = await updateLocalMeasuresCache(
            projectId,
            docKey,
            machineId,
            fieldName,
            (existingArray) => {
              const arr = [...(existingArray ?? [])];
              judgedData = applyClientJudgment(measureType, measureData, measureDiscus);
              arr.push({ ...judgedData, createdAt: new Date().toISOString() });
              newMeasureIndex = arr.length - 1;
              return arr;
            },
            cached
          );

          await addToSyncQueue({
            type: "measure",
            entityId: `${machineId}-${graphqlMeasureType}-create-${Date.now()}`,
            operation: "create",
            payload: {
              measureType: graphqlMeasureType,
              measureIndex: newMeasureIndex,
              measureData: judgedData,
            },
            projectId,
            docKey,
            machineId,
            expectedVersion,
          });

          if (updatedMeasures) {
            setCachedMeasures(updatedMeasures);
          }

          logger.info("오프라인 계측 추가 저장", { measureType });
          return { success: true };
        } catch (err) {
          logger.error("오프라인 계측 추가 실패", err);
          return {
            success: false,
            error: err instanceof Error ? err.message : "로컬 저장 실패",
          };
        }
      }

      // 온라인: 기존 GraphQL mutation
      try {
        const result = await addMeasureMutation({
          variables: {
            input: {
              projectId,
              docKey,
              machineId,
              measureType: graphqlMeasureType as MeasureType,
              data: measureData,
              expectedVersion,
            },
          },
          update(cache, { data: mutationData }) {
            if (!mutationData?.addMeasure.success || !mutationData.addMeasure.measure) return;

            const existingData = cache.readQuery<MachineMeasuresData>({
              query: MACHINE_MEASURES_QUERY,
              variables: { projectId, docKey, machineId },
            });

            if (!existingData?.machineMeasures) return;

            const existingArray =
              (existingData.machineMeasures[fieldName] as Record<string, unknown>[] | undefined) ??
              [];
            const newItem = mutationData.addMeasure.measure as Record<string, unknown>;

            cache.writeQuery({
              query: MACHINE_MEASURES_QUERY,
              variables: { projectId, docKey, machineId },
              data: {
                machineMeasures: {
                  ...existingData.machineMeasures,
                  [fieldName]: [...existingArray, newItem],
                },
              },
            });
          },
        });

        return result.data?.addMeasure ?? { success: false, error: "응답 없음" };
      } catch (err) {
        logger.error("계측 항목 추가 실패", err);
        return {
          success: false,
          error: err instanceof Error ? err.message : "알 수 없는 오류",
        };
      }
    },
    [projectId, docKey, machineId, isOffline, addMeasureMutation]
  );

  // 계측 항목 수정 (온라인: GraphQL mutation, 오프라인: 로컬 캐시 + syncQueue)
  const updateMeasure = useCallback(
    async (
      measureType: MeasureType,
      measureIndex: number,
      measureData: Record<string, unknown>,
      expectedVersion?: number
    ): Promise<MeasureResponse> => {
      if (!machineId) {
        return { success: false, error: "설비를 선택해주세요." };
      }

      const graphqlMeasureType = measureTypeValueToGraphQLKey(measureType);
      const fieldName = measureTypeToFieldName(measureType);

      // 오프라인: 로컬 캐시 즉시 업데이트 + syncQueue에 추가
      if (isOffline) {
        try {
          // 캐시된 measureDiscus 조회 (회사별 판정 코멘트)
          const cached = await getCachedMachine(projectId, docKey, machineId);
          let measureDiscus = cached?.project?.measureDiscus;

          // 오프라인 생성 설비에 measureDiscus가 없으면 같은 프로젝트 다른 설비에서 조회 (#469)
          if (!measureDiscus) {
            const siblings = await getCachedMachinesByProject(projectId);
            measureDiscus = siblings.find((m) => m.project?.measureDiscus)?.project?.measureDiscus;
          }

          // 변경 전 값 캡처 (before/after 표시용)
          let measureOldData: Record<string, unknown> | undefined;
          let judgedData: Record<string, unknown> = measureData;
          const updatedMeasures = await updateLocalMeasuresCache(
            projectId,
            docKey,
            machineId,
            fieldName,
            (existingArray) => {
              const arr = [...(existingArray ?? [])];
              if (measureIndex < arr.length) {
                // 변경되는 필드의 이전 값만 캡처
                const prev = arr[measureIndex] as Record<string, unknown>;
                measureOldData = {};
                for (const key of Object.keys(measureData)) {
                  measureOldData[key] = prev[key];
                }
                const merged = { ...prev, ...measureData };
                const result = applyClientJudgment(measureType, merged, measureDiscus);
                arr[measureIndex] = result;
                // 판정값 포함 데이터 (사용자가 변경한 필드 + 자동 판정 필드)
                judgedData = {};
                for (const key of Object.keys(result)) {
                  if (key in measureData || !(key in prev) || prev[key] !== result[key]) {
                    judgedData[key] = result[key];
                  }
                }
              }
              return arr;
            },
            cached
          );

          // syncQueue에 추가
          await addToSyncQueue({
            type: "measure",
            entityId: `${machineId}-${graphqlMeasureType}-${measureIndex}`,
            operation: "update",
            payload: {
              measureType: graphqlMeasureType,
              measureIndex,
              measureData: judgedData,
              measureOldData,
            },
            projectId,
            docKey,
            machineId,
            expectedVersion,
          });

          // state 업데이트
          if (updatedMeasures) {
            setCachedMeasures(updatedMeasures);
          }

          logger.info("오프라인 계측 수정 저장", { measureType, measureIndex });
          return { success: true };
        } catch (err) {
          logger.error("오프라인 계측 수정 실패", err);
          return {
            success: false,
            error: err instanceof Error ? err.message : "로컬 저장 실패",
          };
        }
      }

      // 온라인: 기존 GraphQL mutation
      try {
        const result = await updateMeasureMutation({
          variables: {
            input: {
              projectId,
              docKey,
              machineId,
              measureType: graphqlMeasureType as MeasureType,
              measureIndex,
              data: measureData,
              expectedVersion,
            },
          },
          update(cache, { data: mutationData }) {
            if (!mutationData?.updateMeasure.success || !mutationData.updateMeasure.measure) return;

            const existingData = cache.readQuery<MachineMeasuresData>({
              query: MACHINE_MEASURES_QUERY,
              variables: { projectId, docKey, machineId },
            });

            if (!existingData?.machineMeasures) return;

            const existingArray = existingData.machineMeasures[fieldName] as
              | Record<string, unknown>[]
              | undefined;
            if (!existingArray) return;

            const updatedArray = [...existingArray];
            const existing = existingArray[measureIndex] as Record<string, unknown>;
            const incoming = mutationData.updateMeasure.measure as Record<string, unknown>;

            updatedArray[measureIndex] = {
              ...existing,
              ...incoming,
              // photo는 deep merge (부분 응답 시 기존 데이터 보존)
              photo: incoming.photo
                ? {
                    ...(existing.photo as Record<string, unknown>),
                    ...(incoming.photo as Record<string, unknown>),
                  }
                : existing.photo,
            };

            cache.writeQuery({
              query: MACHINE_MEASURES_QUERY,
              variables: { projectId, docKey, machineId },
              data: {
                machineMeasures: {
                  ...existingData.machineMeasures,
                  [fieldName]: updatedArray,
                },
              },
            });
          },
        });

        return result.data?.updateMeasure ?? { success: false, error: "응답 없음" };
      } catch (err) {
        logger.error("계측 항목 수정 실패", err);
        return {
          success: false,
          error: err instanceof Error ? err.message : "알 수 없는 오류",
        };
      }
    },
    [projectId, docKey, machineId, isOffline, updateMeasureMutation]
  );

  // 계측 항목 삭제 (온라인: GraphQL mutation, 오프라인: 로컬 캐시 + syncQueue)
  const deleteMeasure = useCallback(
    async (
      measureType: MeasureType,
      measureIndex: number,
      expectedVersion?: number
    ): Promise<MeasureResponse> => {
      if (!machineId) {
        return { success: false, error: "설비를 선택해주세요." };
      }

      const graphqlMeasureType = measureTypeValueToGraphQLKey(measureType);
      const fieldName = measureTypeToFieldName(measureType);

      // 오프라인: 로컬 캐시에서 즉시 제거 + syncQueue에 delete 항목 추가
      if (isOffline) {
        try {
          const updatedMeasures = await updateLocalMeasuresCache(
            projectId,
            docKey,
            machineId,
            fieldName,
            (existingArray) => {
              const arr = [...(existingArray ?? [])];
              return arr.filter((_, idx) => idx !== measureIndex);
            }
          );

          // 삭제될 계측과 연결된 사진 syncQueue 항목 정리 (#458)
          const queue = await getSyncQueue();
          const relevantItems = queue.filter(
            (q) => q.machineId === machineId && q.projectId === projectId && q.docKey === docKey
          );
          await adjustMeasurePhotoQueue(relevantItems, graphqlMeasureType, measureIndex);

          await addToSyncQueue({
            type: "measure",
            entityId: `${machineId}-${graphqlMeasureType}-delete-${measureIndex}`,
            operation: "delete",
            payload: {
              measureType: graphqlMeasureType,
              measureIndex,
            },
            projectId,
            docKey,
            machineId,
            expectedVersion,
          });

          if (updatedMeasures) {
            setCachedMeasures(updatedMeasures);
          }

          logger.info("오프라인 계측 삭제 저장", { measureType, measureIndex });
          return { success: true };
        } catch (err) {
          logger.error("오프라인 계측 삭제 실패", err);
          return {
            success: false,
            error: err instanceof Error ? err.message : "로컬 저장 실패",
          };
        }
      }

      // 온라인: 기존 GraphQL mutation
      try {
        const result = await deleteMeasureMutation({
          variables: {
            input: {
              projectId,
              docKey,
              machineId,
              measureType: graphqlMeasureType as MeasureType,
              measureIndex,
              expectedVersion,
            },
          },
          update(cache, { data: mutationData }) {
            if (!mutationData?.deleteMeasure.success) return;

            const existingData = cache.readQuery<MachineMeasuresData>({
              query: MACHINE_MEASURES_QUERY,
              variables: { projectId, docKey, machineId },
            });

            if (!existingData?.machineMeasures) return;

            const existingArray = existingData.machineMeasures[fieldName] as
              | Record<string, unknown>[]
              | undefined;
            if (!existingArray) return;

            const updatedArray = existingArray.filter((_, idx) => idx !== measureIndex);

            cache.writeQuery({
              query: MACHINE_MEASURES_QUERY,
              variables: { projectId, docKey, machineId },
              data: {
                machineMeasures: {
                  ...existingData.machineMeasures,
                  [fieldName]: updatedArray,
                },
              },
            });
          },
        });

        return result.data?.deleteMeasure ?? { success: false, error: "응답 없음" };
      } catch (err) {
        logger.error("계측 항목 삭제 실패", err);
        return {
          success: false,
          error: err instanceof Error ? err.message : "알 수 없는 오류",
        };
      }
    },
    [projectId, docKey, machineId, isOffline, deleteMeasureMutation]
  );

  // 계측 항목 로컬 캐시만 업데이트 (syncQueue 추가 없음)
  // 오프라인 사진 촬영 시 사용: photo syncQueue 항목과 중복 방지
  const updateMeasureLocalOnly = useCallback(
    async (
      measureType: MeasureType,
      measureIndex: number,
      measureData: Record<string, unknown>
    ): Promise<{ success: boolean; error?: string }> => {
      if (!machineId) {
        return { success: false, error: "설비를 선택해주세요." };
      }

      const fieldName = measureTypeToFieldName(measureType);

      try {
        const updatedMeasures = await updateLocalMeasuresCache(
          projectId,
          docKey,
          machineId,
          fieldName,
          (existingArray) => {
            const arr = [...(existingArray ?? [])];
            if (measureIndex < arr.length) {
              arr[measureIndex] = { ...arr[measureIndex], ...measureData };
            }
            return arr;
          }
        );

        if (updatedMeasures) {
          setCachedMeasures(updatedMeasures);
        }

        return { success: true };
      } catch (err) {
        logger.error("로컬 캐시 업데이트 실패", err);
        return {
          success: false,
          error: err instanceof Error ? err.message : "로컬 저장 실패",
        };
      }
    },
    [projectId, docKey, machineId]
  );

  // 특정 타입의 계측 데이터 가져오기
  const getMeasuresByType = useCallback(
    (measureType: MeasureType): Record<string, unknown>[] => {
      if (!measures) return [];
      const fieldName = measureTypeToFieldName(measureType);
      return (measures[fieldName] as Record<string, unknown>[]) ?? [];
    },
    [measures]
  );

  // 전체 계측 항목 수 계산
  const getTotalMeasureCount = useCallback((): number => {
    if (!measures) return 0;
    let count = 0;
    for (const key of Object.keys(measures) as MeasuresFieldName[]) {
      const arr = measures[key];
      if (Array.isArray(arr)) {
        count += arr.length;
      }
    }
    return count;
  }, [measures]);

  // 로딩: 캐시 로딩 중이거나 (온라인이고 쿼리 로딩 중이면서 캐시가 없을 때)
  const loading = cacheLoading || (!isOffline && queryLoading && !cachedMeasures);
  const error = isOffline ? null : (queryError ?? null);

  return {
    measures,
    loading,
    error,
    isFromCache,
    refetch,
    addMeasure,
    updateMeasure,
    updateMeasureLocalOnly,
    deleteMeasure,
    getMeasuresByType,
    getTotalMeasureCount,
    mutationLoading: addLoading || updateLoading || deleteLoading,
  };
}
